import { useQuery } from "@tanstack/react-query"
import { Download } from "lucide-react"
import { useEffect, useMemo } from "react"

import { pedirBinario } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Modal } from "@/componentes/ui/Modal"

export type ArchivoParaVer =
  | {
      /** La ruta autenticada del contenido (regla 8), nunca un estático. */
      ruta: string
      nombre: string
    }
  | {
      /** Un archivo elegido que todavía no se ha subido: se ve desde el navegador. */
      local: Blob
      nombre: string
    }

/**
 * Un soporte a la vista, sin salir de la pantalla.
 *
 * ⚠ **Se ve en un modal y no en otra pestaña.** Abrir el enlace sacaba de la ficha y
 * obligaba a volver y recargar para seguir revisando: para echar un vistazo a una
 * factura se perdía el sitio. Aquí se cierra y se sigue donde se estaba.
 *
 * ⚠ **Se pide por `pedirBinario`, con la sesión**, y se enseña desde un `blob:`. Un
 * `<img src>` a la ruta de la API también llevaría la cookie, pero un error
 * (sin permiso, archivo perdido) saldría como imagen rota en vez de con su mensaje.
 *
 * ⚠ **También lo que aún no se ha subido** (`local`): la captura recién pegada se
 * revisa antes de mandarla, sin ida y vuelta al servidor.
 *
 * Imágenes y PDF se ven dentro; lo demás —Excel, Word— no tiene visor en el
 * navegador y ofrece descargarlo, que es lo único honesto que se puede hacer.
 */
export function VistaPreviaArchivo({
  archivo,
  onCerrar,
}: {
  archivo: ArchivoParaVer | null
  onCerrar: () => void
}) {
  const ruta = archivo && "ruta" in archivo ? archivo.ruta : null
  const local = archivo && "local" in archivo ? archivo.local : null
  const remoto = useQuery({
    queryKey: ["archivo", ruta],
    queryFn: () => pedirBinario(ruta ?? ""),
    enabled: ruta !== null,
    staleTime: 5 * 60_000,
  })
  const contenido = {
    data: local ?? remoto.data,
    isPending: local === null && remoto.isPending,
    isError: local === null && remoto.isError,
    error: remoto.error,
  }

  const url = useMemo(
    () => (contenido.data ? URL.createObjectURL(contenido.data) : null),
    [contenido.data],
  )
  // Se suelta al cambiar de archivo o al cerrar: cada `blob:` retiene el archivo
  // entero en memoria mientras exista.
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url])

  const tipo = contenido.data?.type ?? ""
  const esImagen = tipo.startsWith("image/")
  const esPdf = tipo === "application/pdf"

  return (
    <Modal abierto={archivo !== null} titulo={archivo?.nombre ?? ""} onCerrar={onCerrar} ancho="lg">
      {contenido.isPending && <EstadoCarga etiqueta="Abriendo el archivo…" />}
      {contenido.isError && (
        <EstadoError titulo="No se pudo abrir el archivo." error={contenido.error} />
      )}
      {url && esImagen && (
        <img
          src={url}
          alt={archivo?.nombre ?? "Soporte"}
          className="mx-auto max-h-[70vh] w-auto max-w-full object-contain"
        />
      )}
      {url && esPdf && (
        <iframe src={url} title={archivo?.nombre ?? "Soporte"} className="h-[70vh] w-full" />
      )}
      {url && !esImagen && !esPdf && (
        <p className="py-4 text-sm" style={{ color: "var(--fg-muted)" }}>
          Este tipo de archivo no se puede ver en el navegador.
        </p>
      )}
      {url && (
        <div className="mt-3 flex justify-end">
          <Boton
            tamano="sm"
            onClick={() => {
              const enlace = document.createElement("a")
              enlace.href = url
              enlace.download = archivo?.nombre ?? "soporte"
              enlace.click()
            }}
          >
            <Download size={14} /> Descargar
          </Boton>
        </div>
      )}
    </Modal>
  )
}
